import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccountingService } from '../../services/accounting.service';
import { useToast } from '../../context/ToastContext';
import { AccountType, IAccount } from '../../interfaces/accounting.interface';
import AccountSelect from './AccountSelect';
import { today } from './money';

export type MoneyMode = 'in' | 'out' | 'transfer';

interface Props {
    open: boolean;
    mode: MoneyMode;
    institutionId: string;
    accounts: IAccount[];
    onClose: () => void;
    onSaved: () => void;
}

// Where the other side of a movement may go. Money in: income, a loan or owner's capital.
// Money out: expenses, repaying a liability, an advance, or owner's drawings.
const CATEGORY_TYPES: Record<'in' | 'out', AccountType[]> = {
    in: ['INCOME', 'LIABILITY', 'EQUITY'],
    out: ['EXPENSE', 'LIABILITY', 'ASSET', 'EQUITY'],
};

const MoneyDialog: React.FC<Props> = ({ open, mode, institutionId, accounts, onClose, onSaved }) => {
    const { t } = useTranslation(['accounting', 'common']);
    const { showSuccess, showApiError } = useToast();
    const [date, setDate] = useState(today());
    const [moneyAccount, setMoneyAccount] = useState('');
    const [otherAccount, setOtherAccount] = useState('');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setDate(today());
            setMoneyAccount('');
            setOtherAccount('');
            setAmount('');
            setDescription('');
        }
    }, [open, mode]);

    const cashAccounts = accounts.filter(a => a.cashOrBank);
    const categories = mode === 'transfer' ? [] : accounts.filter(a => !a.cashOrBank && CATEGORY_TYPES[mode].includes(a.type));
    const value = parseFloat(amount);
    const valid = !!date && !!moneyAccount && !!otherAccount && value > 0
        && (mode === 'transfer' ? moneyAccount !== otherAccount : !!description.trim());

    const save = async () => {
        setSaving(true);
        try {
            if (mode === 'transfer') {
                await AccountingService.transfer(institutionId, {
                    entryDate: date, fromAccountId: moneyAccount, toAccountId: otherAccount, amount: value,
                    description: description.trim() || undefined,
                });
            } else {
                const body = { entryDate: date, cashAccountId: moneyAccount, otherAccountId: otherAccount, amount: value,
                    description: description.trim() };
                await (mode === 'in' ? AccountingService.moneyIn(institutionId, body) : AccountingService.moneyOut(institutionId, body));
            }
            showSuccess(t(`moneyDialog.recorded.${mode}`));
            onSaved();
        } catch (error) {
            showApiError(error, t(`moneyDialog.recordFailed.${mode}`));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t(`moneyDialog.title.${mode}`)}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField id="money-date" label={t('fields.date')} type="date" size="small" value={date}
                               onChange={e => setDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    {mode === 'transfer' ? (
                        <>
                            <AccountSelect id="money-from" label={t('fields.from')} value={moneyAccount} accounts={cashAccounts} onChange={setMoneyAccount} />
                            <AccountSelect id="money-to" label={t('fields.to')} value={otherAccount} accounts={cashAccounts} onChange={setOtherAccount} />
                        </>
                    ) : (
                        <>
                            <AccountSelect id="money-account" label={mode === 'in' ? t('moneyDialog.receivedInto') : t('moneyDialog.paidFrom')}
                                           value={moneyAccount} accounts={cashAccounts} onChange={setMoneyAccount} />
                            <AccountSelect id="money-category" label={t('moneyDialog.category')} value={otherAccount} accounts={categories}
                                           onChange={setOtherAccount} />
                        </>
                    )}
                    <TextField id="money-amount" label={t('fields.amountBdt')} type="number" size="small" value={amount}
                               onChange={e => setAmount(e.target.value)} inputProps={{ min: 0, step: '0.01' }} />
                    <TextField id="money-description" label={t('fields.description')} size="small" value={description}
                               onChange={e => setDescription(e.target.value)} inputProps={{ maxLength: 255 }}
                               required={mode !== 'transfer'} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common:actions.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={!valid || saving}>{t('common:actions.save')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default MoneyDialog;
